import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Marker, Popup } from '@vis.gl/react-maplibre';
import MapLibreMap from '../../components/maps/MapLibreMap';
import { supabase } from '../../supabaseClient';
import { guardLocationService } from '../../services/guardLocationService';
import type { GuardLocation, StaleTrackingExecution } from '../../services/guardLocationService';
import { getBoundsFromPositions } from '../../utils/geo';
import type { LatLngTuple } from '../../utils/geo';
import { getAvatarAccentStyle } from '../../utils/avatarAccent';
import ShiftHistoryPage from './ShiftHistoryPage';
import { alarmService } from '../../services/alarmService';
import type { Alarm, AlarmLocation } from '../../services/alarmService';
import { buildAlarmPins } from '../../utils/alarmPins';
import type { AlarmPin, PlacedAlarm } from '../../utils/alarmPins';
import type { MapRef } from '@vis.gl/react-maplibre';
import {
  FiAlertTriangle,
  FiArrowRight,
  FiCrosshair,
  FiEye,
  FiEyeOff,
  FiMapPin,
  FiRadio,
  FiUsers,
} from 'react-icons/fi';
import { getLocale, t, tn, useI18n } from '../../i18n';
import './ActivityPage.css';

const getAvatarUrl = (avatarPath: string | null | undefined): string | null => {
  if (!avatarPath) return null;
  if (avatarPath.startsWith('http')) return avatarPath;
  const { data } = supabase.storage.from('avatars').getPublicUrl(avatarPath);
  return data.publicUrl;
};

const getGuardKey = (guard: GuardLocation, prefix: string): string => (
  `${prefix}-${guard.id ?? guard.guardId ?? guard.fullName ?? 'guard'}-${guard.updatedAt ?? 'na'}`
);

const formatPingAge = (ageMs: number | null): string => {
  if (ageMs === null) return t('unknown');
  const minutes = Math.max(0, Math.round(ageMs / 60000));
  if (minutes < 60) return t('{count} min', { count: minutes });
  return t('{hours} h {minutes} min', { hours: Math.floor(minutes / 60), minutes: minutes % 60 });
};

const formatAlarmTime = (ts: string): string => new Date(ts).toLocaleString(getLocale(), {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

// Says which of the three position sources a pin is drawn from, because only
// the first is where the alarm actually happened.
const describeAlarmPosition = ({ source, accuracyM, positionedAt }: PlacedAlarm): string => {
  if (source === 'trigger') {
    return accuracyM != null
      ? t('Position when the alarm was raised (±{metres} m)', { metres: Math.round(accuracyM) })
      : t('Position when the alarm was raised');
  }

  if (source === 'guard') {
    const reportedAtMs = positionedAt ? Date.parse(positionedAt) : Number.NaN;
    return Number.isNaN(reportedAtMs)
      ? t('Last position reported by the guard')
      : t('Last position reported by the guard, {age} ago', { age: formatPingAge(Date.now() - reportedAtMs) });
  }

  return t('Site location — no GPS position for this alarm');
};

interface Props {
  activeAlarms?: Alarm[];
  activeTab?: 'live' | 'history';
  historyNavigationTarget?: {
    shiftExecutionId: string;
    nonce: number;
  } | null;
  onHistoryNavigationHandled?: () => void;
  onOpenAlarm?: (alarmId: string) => void;
}

export default function ActivityPage({
  activeAlarms = [],
  activeTab = 'live',
  historyNavigationTarget = null,
  onHistoryNavigationHandled,
  onOpenAlarm,
}: Props) {
  useI18n();
  const [guards, setGuards] = useState<GuardLocation[]>([]);
  const [silentShifts, setSilentShifts] = useState<StaleTrackingExecution[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedGuard, setSelectedGuard] = useState<GuardLocation | null>(null);
  const [lastLocationGuard, setLastLocationGuard] = useState<GuardLocation | null>(null);
  const [alarmLocations, setAlarmLocations] = useState<AlarmLocation[]>([]);
  // On by default: an operator should not have to go looking for an emergency.
  const [showAlarms, setShowAlarms] = useState(true);
  const [selectedAlarmId, setSelectedAlarmId] = useState<string | null>(null);
  const mapRef = useRef<MapRef | null>(null);
  const isMountedRef = useRef(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const defaultViewState = {
    latitude: 53.3498,
    longitude: -6.2603,
    zoom: 13,
  };

  // Set of guard profile IDs that have an active (open/acknowledged) alarm
  const alarmedGuardIds = useMemo(() => {
    return new Set(activeAlarms.map((a) => a.triggered_by));
  }, [activeAlarms]);

  const sortedGuards = useMemo(() => {
    return [...guards].sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? '', undefined, { sensitivity: 'base' }));
  }, [guards]);

  const siteOptions = useMemo(() => {
    const siteMap = new Map<string, string>();
    sortedGuards.forEach((guard) => {
      if (!guard.siteId) return;
      if (!siteMap.has(guard.siteId)) {
        siteMap.set(guard.siteId, guard.siteName ?? t('Unassigned'));
      }
    });

    return [...siteMap.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [sortedGuards]);

  const filteredGuards = useMemo(() => {
    if (selectedSiteId === 'all') return sortedGuards;
    if (selectedSiteId === 'unassigned') return sortedGuards.filter((guard) => !guard.siteId);
    return sortedGuards.filter((guard) => guard.siteId === selectedSiteId);
  }, [sortedGuards, selectedSiteId]);

  const onlineGuards = useMemo(() => filteredGuards.filter((guard) => guard.isOnShift), [filteredGuards]);
  const offlineGuards = useMemo(() => filteredGuards.filter((guard) => !guard.isOnShift), [filteredGuards]);
  const selectedGuardLive = useMemo(
    () => (selectedGuard ? onlineGuards.find((guard) => guard.id === selectedGuard.id) ?? null : null),
    [onlineGuards, selectedGuard],
  );

  const visibleAlarms = useMemo(() => {
    if (selectedSiteId === 'all') return activeAlarms;
    if (selectedSiteId === 'unassigned') return activeAlarms.filter((alarm) => !alarm.site_id);
    return activeAlarms.filter((alarm) => alarm.site_id === selectedSiteId);
  }, [activeAlarms, selectedSiteId]);

  const { pins: alarmPins, unplaced: unplacedAlarms } = useMemo(
    () => buildAlarmPins(visibleAlarms, alarmLocations, guards),
    [visibleAlarms, alarmLocations, guards],
  );

  // Looked up by alarm rather than held as a pin, so the popup follows a pin
  // that moves and closes by itself once the alarm is resolved.
  const selectedAlarmPin = useMemo(() => {
    if (!showAlarms || !selectedAlarmId) return null;
    return alarmPins.find((pin) => pin.entries.some((entry) => entry.alarm.id === selectedAlarmId)) ?? null;
  }, [alarmPins, selectedAlarmId, showAlarms]);

  const activeAlarmIdsKey = useMemo(
    () => activeAlarms.map((alarm) => alarm.id).sort().join(','),
    [activeAlarms],
  );

  const loadGuardLocations = useCallback(async () => {
    try {
      // Fetched together so the map and the silent-shift banner never disagree
      // about who is reporting.
      const [locations, silent] = await Promise.all([
        guardLocationService.fetchGuardLocations(),
        guardLocationService.fetchStaleTrackingExecutions(),
      ]);
      if (isMountedRef.current) {
        setGuards(locations);
        setSilentShifts(silent);
      }
    } catch (err) {
      console.error('Error loading guard locations:', (err as Error).message);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadGuardLocations();

    const connectRealtime = () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }

      channelRef.current = supabase
        .channel(`activity_guard_locations_${Date.now()}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'guard_live_locations',
          },
          () => {
            loadGuardLocations();
          },
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            loadGuardLocations();
            return;
          }

          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            if (reconnectTimerRef.current) {
              clearTimeout(reconnectTimerRef.current);
            }
            reconnectTimerRef.current = setTimeout(() => {
              if (isMountedRef.current) {
                connectRealtime();
              }
            }, 2000);
          }
        });
    };

    connectRealtime();
    const pollingInterval = setInterval(loadGuardLocations, 10000);

    return () => {
      isMountedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      clearInterval(pollingInterval);
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [loadGuardLocations]);

  useEffect(() => {
    if (activeTab !== 'live' || !activeAlarmIdsKey) {
      setAlarmLocations((previous) => (previous.length ? [] : previous));
      return undefined;
    }

    let cancelled = false;
    const alarmIds = activeAlarmIdsKey.split(',');

    const loadAlarmLocations = async () => {
      try {
        const locations = await alarmService.fetchAlarmLocations(alarmIds);
        if (!cancelled) setAlarmLocations(locations);
      } catch (err) {
        // Not fatal: an alarm without its ping is still drawn, at the guard or the site.
        console.error('Error loading alarm locations:', (err as Error).message);
      }
    };

    void loadAlarmLocations();
    // The ping is written just after the alarm row, so the first read can miss it.
    const pollingInterval = setInterval(() => { void loadAlarmLocations(); }, 10000);

    return () => {
      cancelled = true;
      clearInterval(pollingInterval);
    };
  }, [activeTab, activeAlarmIdsKey]);

  useEffect(() => {
    if (selectedGuard && !selectedGuardLive) {
      setSelectedGuard(null);
    }
  }, [selectedGuard, selectedGuardLive]);

  // Clear last-location pin when the guard comes back online
  useEffect(() => {
    if (lastLocationGuard && onlineGuards.some((g) => g.id === lastLocationGuard.id)) {
      setLastLocationGuard(null);
    }
  }, [onlineGuards, lastLocationGuard]);

  const formatTimestamp = (ts: string | null | undefined): string => {
    if (!ts) return t('N/A');
    const date = new Date(ts);
    return date.toLocaleTimeString(getLocale());
  };

  const visibleSilentShifts = useMemo(() => {
    if (selectedSiteId === 'all') return silentShifts;
    if (selectedSiteId === 'unassigned') return silentShifts.filter((shift) => !shift.siteId);
    return silentShifts.filter((shift) => shift.siteId === selectedSiteId);
  }, [silentShifts, selectedSiteId]);

  const fitMapTo = (positions: LatLngTuple[]) => {
    if (!mapRef.current || positions.length === 0) return;

    if (positions.length === 1) {
      const [[latitude, longitude]] = positions;
      (mapRef.current as unknown as { flyTo: (opts: object) => void }).flyTo({
        center: [longitude, latitude],
        zoom: 15,
        duration: 1000,
      });
      return;
    }

    const bounds = getBoundsFromPositions(positions);
    if (bounds) {
      (mapRef.current as unknown as { fitBounds: (b: unknown, opts: object) => void }).fitBounds(bounds, { padding: 50, duration: 1000 });
    }
  };

  const centerOnGuards = () => {
    fitMapTo(onlineGuards.map((guard) => [guard.latitude, guard.longitude] as LatLngTuple));
  };

  const toggleAlarms = () => {
    if (showAlarms) {
      setShowAlarms(false);
      setSelectedAlarmId(null);
      return;
    }

    setShowAlarms(true);
    // Switching the layer on is a request to see the alarms, wherever the map was left.
    fitMapTo(alarmPins.map((pin) => [pin.latitude, pin.longitude] as LatLngTuple));
  };

  const renderAlarmPopup = (pin: AlarmPin) => (
    <div className="activity__alarm-popup">
      {pin.entries.length > 1 && (
        <div className="map-popup__eyebrow">
          {t('{count} alarms at this position', { count: pin.entries.length })}
        </div>
      )}
      {pin.entries.map((entry) => {
        const { alarm } = entry;
        return (
          <div className="activity__alarm-entry" key={alarm.id}>
            <div className={`activity__alarm-status activity__alarm-status--${alarm.status}`}>
              <span>{alarm.status === 'acknowledged' ? t('Acknowledged||alarm') : t('Open||alarm')}</span>
              {alarm.metadata?.source === 'welfare_check' && <span>{t('Welfare check')}</span>}
            </div>
            <div className="map-popup__title">{alarm.title ?? t('Alarm')}</div>
            {alarm.message && <div className="map-popup__text">{alarm.message}</div>}
            <div className="map-popup__text">
              {t('Guard: {name}', { name: alarm.profiles?.full_name ?? t('Unknown Guard') })}
            </div>
            <div className="map-popup__text">
              {t('Site: {name}', { name: alarm.sites?.name ?? t('Unknown Site') })}
            </div>
            <div className="map-popup__text">
              {t('Triggered: {time}', { time: formatAlarmTime(alarm.triggered_at) })}
            </div>
            <div className="map-popup__text activity__alarm-position">{describeAlarmPosition(entry)}</div>
            {onOpenAlarm && (
              <button
                type="button"
                className="activity__alarm-link"
                onClick={() => onOpenAlarm(alarm.id)}
              >
                {t('View alarm')}
                <FiArrowRight aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderGuardCard = (guard: GuardLocation) => {
    const isAlarmed = alarmedGuardIds.has(guard.guardId);
    const isOnShift = Boolean(guard.isOnShift);
    // On shift but silent: the guard has not gone offline, their device has
    // stopped reporting. Those are different problems and must read differently.
    const hasLostSignal = isOnShift && Boolean(guard.isLocationStale);
    const focusGuard = () => {
      if (!isOnShift) return;
      setSelectedGuard(guard);
      setSelectedAlarmId(null);
      mapRef.current?.flyTo({
        center: [guard.longitude, guard.latitude],
        zoom: 15,
        duration: 1000,
      });
    };

    return (
      <div
        onClick={focusGuard}
        onKeyDown={(event) => {
          if (!isOnShift || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          focusGuard();
        }}
        role={isOnShift ? 'button' : undefined}
        tabIndex={isOnShift ? 0 : undefined}
        aria-pressed={isOnShift ? selectedGuard?.id === guard.id : undefined}
        className={[
          'activity__guard-card',
          selectedGuard?.id === guard.id ? 'activity__guard-card--selected' : '',
          isAlarmed ? 'activity__guard-card--alarm' : '',
          !isOnShift ? 'activity__guard-card--inactive' : '',
          hasLostSignal ? 'activity__guard-card--stale' : '',
        ].join(' ').trim()}
      >
        {isAlarmed && <div className="activity__alarm-label">{t('ALARM')}</div>}
        <div className="activity__guard-header">
          <div className={`activity__guard-avatar-wrap ${isAlarmed ? 'activity__guard-avatar-wrap--alarm' : ''}`}>
            {guard.avatarUrl ? (
              <img
                src={getAvatarUrl(guard.avatarUrl) ?? ''}
                alt={guard.fullName}
                className="activity__guard-avatar"
              />
            ) : (
              <div
                className="activity__guard-avatar activity__guard-avatar--placeholder"
                style={getAvatarAccentStyle(guard.id ?? guard.guardId ?? guard.fullName)}
              >
                {guard.fullName?.charAt(0)?.toUpperCase() ?? '?'}
              </div>
            )}
          </div>
          <div className="activity__guard-name">{guard.fullName}</div>
          <div
            className={`activity__guard-status ${
              hasLostSignal
                ? 'activity__guard-status--stale'
                : isOnShift
                  ? 'activity__guard-status--active'
                  : 'activity__guard-status--inactive'
            }`}
          >
            {hasLostSignal ? t('No signal') : isOnShift ? t('Online') : t('Offline')}
          </div>
        </div>
        <div className="activity__guard-meta">
          {t('Site: {name}', { name: guard.siteName ?? t('Unassigned') })}
        </div>
        <div className="activity__guard-meta">
          {t('Last update: {time}', { time: formatTimestamp(guard.updatedAt) })}
        </div>
        {hasLostSignal && (
          <div className="activity__guard-meta activity__guard-meta--stale">
            {t('No position for {age}', { age: formatPingAge(guard.lastPingAgeMs) })}
          </div>
        )}
        {guard.battery != null && (
          <div className="activity__guard-meta">
            {t('Battery: {level}%', { level: guard.battery })}
          </div>
        )}
        {!isOnShift && (guard.latitude !== 0 || guard.longitude !== 0) && (
          <button
            type="button"
            className={`activity__last-location-btn${lastLocationGuard?.id === guard.id ? ' activity__last-location-btn--active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (lastLocationGuard?.id === guard.id) {
                setLastLocationGuard(null);
                return;
              }
              setLastLocationGuard(guard);
              setSelectedAlarmId(null);
              if (mapRef.current) {
                (mapRef.current as unknown as { flyTo: (opts: object) => void }).flyTo({
                  center: [guard.longitude, guard.latitude],
                  zoom: 15,
                  duration: 1000,
                });
              }
            }}
          >
            {lastLocationGuard?.id === guard.id ? t('Hide last location') : t('Show last location')}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="activity">
      <div className="activity__hero">
        <div>
          <p className="activity__eyebrow">{t('Live operations')}</p>
          <h1 className="activity__title">{t('Guard activity')}</h1>
          <p className="activity__summary">
            {t('Monitor field teams in real time, filter by site and review shift history from one operational view.')}
          </p>
        </div>
        <div className="activity__hero-stats" aria-label={t('Live operations')}>
          <div className="activity__hero-stat">
            <span className="activity__hero-stat-icon activity__hero-stat-icon--online" aria-hidden="true"><FiRadio /></span>
            <span>
              <span className="activity__hero-label">{t('Online')}</span>
              <strong className="activity__hero-value">{onlineGuards.length}</strong>
            </span>
          </div>
          <div className="activity__hero-stat">
            <span className="activity__hero-stat-icon" aria-hidden="true"><FiUsers /></span>
            <span>
              <span className="activity__hero-label">{t('Offline')}</span>
              <strong className="activity__hero-value">{offlineGuards.length}</strong>
            </span>
          </div>
          <div className="activity__hero-stat">
            <span className="activity__hero-stat-icon activity__hero-stat-icon--alarm" aria-hidden="true"><FiAlertTriangle /></span>
            <span>
              <span className="activity__hero-label">{t('Active alarms')}</span>
              <strong className="activity__hero-value">{visibleAlarms.length}</strong>
            </span>
          </div>
        </div>
      </div>

      {activeTab === 'history' && (
        <ShiftHistoryPage
          navigationTarget={historyNavigationTarget}
          onNavigationHandled={onHistoryNavigationHandled}
        />
      )}

      {activeTab === 'live' && <div className="activity__live">
        <div className="activity__map">
          <MapLibreMap
            initialViewState={defaultViewState}
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
            onClick={() => { setSelectedGuard(null); setLastLocationGuard(null); setSelectedAlarmId(null); }}
          >
            {onlineGuards.map((guard) => {
              const isAlarmed = alarmedGuardIds.has(guard.guardId);
              const isStale = Boolean(guard.isLocationStale);
              const avatarUrl = getAvatarUrl(guard.avatarUrl);
              const initial = guard.fullName?.trim()?.charAt(0)?.toUpperCase() ?? '?';

              return (
                <Marker
                  key={getGuardKey(guard, 'map-marker')}
                  latitude={guard.latitude}
                  longitude={guard.longitude}
                  anchor="bottom"
                >
                  <button
                    type="button"
                    className="avatar-marker__button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedGuard(guard);
                      setSelectedAlarmId(null);
                    }}
                    title={isStale
                      ? t('{name} — no signal for {age}', { name: guard.fullName, age: formatPingAge(guard.lastPingAgeMs) })
                      : guard.fullName}
                    aria-label={t('View {name}', { name: guard.fullName })}
                  >
                    <div className={`avatar-marker ${isAlarmed ? 'avatar-marker--alarm' : ''} ${isStale ? 'avatar-marker--stale' : ''}`}>
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={guard.fullName}
                          className={`avatar-marker__img ${isAlarmed ? 'avatar-marker__img--alarm' : ''}`}
                        />
                      ) : (
                        <div
                          className={`avatar-marker__placeholder ${isAlarmed ? 'avatar-marker__placeholder--alarm' : ''}`}
                          style={getAvatarAccentStyle(guard.id ?? guard.guardId ?? guard.fullName)}
                        >
                          {initial}
                        </div>
                      )}
                    </div>
                  </button>
                </Marker>
              );
            })}
            {lastLocationGuard && (
              <Marker
                latitude={lastLocationGuard.latitude}
                longitude={lastLocationGuard.longitude}
                anchor="bottom"
              >
                <button
                  type="button"
                  className="avatar-marker__button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setLastLocationGuard(lastLocationGuard);
                  }}
                  title={t('{name} — last known location', { name: lastLocationGuard.fullName })}
                  aria-label={t('Last known location of {name}', { name: lastLocationGuard.fullName })}
                >
                  <div className="avatar-marker avatar-marker--offline">
                    {getAvatarUrl(lastLocationGuard.avatarUrl) ? (
                      <img
                        src={getAvatarUrl(lastLocationGuard.avatarUrl) ?? ''}
                        alt={lastLocationGuard.fullName}
                        className="avatar-marker__img avatar-marker__img--offline"
                      />
                    ) : (
                      <div
                        className="avatar-marker__placeholder avatar-marker__placeholder--offline"
                        style={getAvatarAccentStyle(lastLocationGuard.id ?? lastLocationGuard.guardId ?? lastLocationGuard.fullName)}
                      >
                        {lastLocationGuard.fullName?.charAt(0)?.toUpperCase() ?? '?'}
                      </div>
                    )}
                  </div>
                </button>
              </Marker>
            )}
            {/* Drawn after the guards so a pin is never buried under the avatar it sits beside. */}
            {showAlarms && alarmPins.map((pin) => {
              const isOpen = pin.entries.some((entry) => entry.alarm.status === 'open');
              const label = pin.entries.length > 1
                ? t('{count} alarms at this position', { count: pin.entries.length })
                : t('View alarm: {title}', { title: pin.entries[0].alarm.title ?? t('Alarm') });

              return (
                <Marker
                  key={`alarm-pin-${pin.key}`}
                  latitude={pin.latitude}
                  longitude={pin.longitude}
                  anchor="center"
                >
                  <button
                    type="button"
                    className={`activity__alarm-pin ${isOpen ? '' : 'activity__alarm-pin--acked'}`.trim()}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedGuard(null);
                      setLastLocationGuard(null);
                      setSelectedAlarmId(pin.key);
                    }}
                    title={label}
                    aria-label={label}
                  >
                    <FiAlertTriangle aria-hidden="true" />
                    {pin.entries.length > 1 && (
                      <span className="activity__alarm-pin-count" aria-hidden="true">{pin.entries.length}</span>
                    )}
                  </button>
                </Marker>
              );
            })}
            {selectedAlarmPin && (
              <Popup
                latitude={selectedAlarmPin.latitude}
                longitude={selectedAlarmPin.longitude}
                anchor="top"
                offset={20}
                maxWidth="300px"
                closeOnClick={false}
                onClose={() => setSelectedAlarmId(null)}
              >
                {renderAlarmPopup(selectedAlarmPin)}
              </Popup>
            )}
            {lastLocationGuard && (
              <Popup
                latitude={lastLocationGuard.latitude}
                longitude={lastLocationGuard.longitude}
                anchor="top"
                offset={20}
                closeOnClick={false}
                onClose={() => setLastLocationGuard(null)}
              >
                <div className="map-popup__eyebrow">{t('Last known location')}</div>
                <div className="map-popup__title">{lastLocationGuard.fullName}</div>
                <div className="map-popup__text">{t('Site: {name}', { name: lastLocationGuard.siteName ?? t('Unassigned') })}</div>
                <div className="map-popup__text">{t('Last seen: {time}', { time: formatTimestamp(lastLocationGuard.updatedAt) })}</div>
                {lastLocationGuard.battery != null && (
                  <div className="map-popup__text">{t('Battery: {level}%', { level: lastLocationGuard.battery })}</div>
                )}
              </Popup>
            )}
            {selectedGuardLive && (
              <Popup
                latitude={selectedGuardLive.latitude}
                longitude={selectedGuardLive.longitude}
                anchor="top"
                offset={20}
                closeOnClick={false}
                onClose={() => setSelectedGuard(null)}
              >
                {alarmedGuardIds.has(selectedGuardLive.guardId) && (
                  <div className="map-popup__text map-popup__text--alarm">{t('ALARM ACTIVE')}</div>
                )}
                {selectedGuardLive.isLocationStale && (
                  <div className="map-popup__text map-popup__text--stale">
                    {t('No signal for {age} — position may be out of date', { age: formatPingAge(selectedGuardLive.lastPingAgeMs) })}
                  </div>
                )}
                <div className="map-popup__title">{selectedGuardLive.fullName}</div>
                <div className="map-popup__text">{t('Site: {name}', { name: selectedGuardLive.siteName ?? t('Unassigned') })}</div>
                <div className="map-popup__text">{t('Last update: {time}', { time: formatTimestamp(selectedGuardLive.updatedAt) })}</div>
                {selectedGuardLive.speed != null && (
                  <div className="map-popup__text">{t('Speed: {speed} m/s', { speed: selectedGuardLive.speed.toFixed(1) })}</div>
                )}
                {selectedGuardLive.battery != null && (
                  <div className="map-popup__text">{t('Battery: {level}%', { level: selectedGuardLive.battery })}</div>
                )}
              </Popup>
            )}
          </MapLibreMap>
          <div className="activity__map-status" aria-live="polite">
            <span className="activity__map-status-dot" aria-hidden="true" />
            <span>{t('Online ({count})', { count: onlineGuards.length })}</span>
          </div>
          <button
            type="button"
            className={[
              'activity__map-alarms',
              showAlarms ? 'activity__map-alarms--on' : '',
              visibleAlarms.length > 0 ? 'activity__map-alarms--active' : '',
            ].join(' ').trim()}
            onClick={toggleAlarms}
            aria-pressed={showAlarms}
            title={showAlarms ? t('Hide alarms on the map') : t('Show alarms on the map')}
          >
            {showAlarms ? <FiEye aria-hidden="true" /> : <FiEyeOff aria-hidden="true" />}
            {t('Alarms')}
            <span className="activity__map-alarms-count">{visibleAlarms.length}</span>
          </button>
          {showAlarms && unplacedAlarms.length > 0 && (
            <div className="activity__map-alarms-note" role="status">
              {tn('{count} alarm has no known position', '{count} alarms have no known position', unplacedAlarms.length)}
            </div>
          )}
          <button
            type="button"
            className="activity__map-centre"
            onClick={centerOnGuards}
            disabled={onlineGuards.length === 0}
          >
            <FiCrosshair aria-hidden="true" />
            {t('Center on Guards')}
          </button>
        </div>

        <aside className="activity__sidebar" aria-label={t('Guards ({count})', { count: filteredGuards.length })}>
          <div className="activity__sidebar-toolbar">
            <div className="activity__sidebar-heading">
              <span className="activity__sidebar-icon" aria-hidden="true"><FiMapPin /></span>
              <div>
                <h2 className="activity__sidebar-title">{t('Guards ({count})', { count: filteredGuards.length })}</h2>
                <div className="activity__sidebar-subtitle">
                  {t('Online: {online} | Offline: {offline}', { online: onlineGuards.length, offline: offlineGuards.length })}
                </div>
              </div>
            </div>

            {visibleSilentShifts.length > 0 && (
              <div className="activity__silent-banner" role="status">
                <div className="activity__silent-banner-title">
                  {tn('{count} shift not reporting', '{count} shifts not reporting', visibleSilentShifts.length)}
                </div>
                <ul className="activity__silent-banner-list">
                  {visibleSilentShifts.map((shift) => (
                    <li key={shift.shiftExecutionId}>
                      {shift.lastLocationAt
                        ? t('{name} — no position for {age}', {
                            name: shift.guardName,
                            age: formatPingAge(shift.silentForSeconds * 1000),
                          })
                        : t('{name} — no position since clock-in', { name: shift.guardName })}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="activity__filters">
              <label className="activity__filter-label" htmlFor="activity-site-filter">{t('Filter by site')}</label>
              <select
                id="activity-site-filter"
                className="activity__filter-select"
                value={selectedSiteId}
                onChange={(e) => setSelectedSiteId(e.target.value)}
              >
                <option value="all">{t('All sites')}</option>
                <option value="unassigned">{t('Unassigned')}</option>
                {siteOptions.map((site) => (
                  <option key={site.id} value={site.id}>{site.name}</option>
                ))}
              </select>
            </div>

          </div>

          <div className="activity__guard-list">
            {isLoading ? (
              <div className="activity__skeleton" aria-hidden="true">
                <span /><span /><span />
              </div>
            ) : filteredGuards.length === 0 ? (
              <div className="activity__empty">
                <FiUsers aria-hidden="true" />
                <p>{t('No guards for this site filter')}</p>
              </div>
            ) : (
              <>
                <div className="activity__section">
                  <div className="activity__section-title">{t('Online ({count})', { count: onlineGuards.length })}</div>
                  {onlineGuards.length ? onlineGuards.map((guard) => (
                    <React.Fragment key={getGuardKey(guard, 'online-card')}>
                      {renderGuardCard(guard)}
                    </React.Fragment>
                  )) : <p className="text-muted">{t('No online guards')}</p>}
                </div>
                <div className="activity__section">
                  <div className="activity__section-title">{t('Offline ({count})', { count: offlineGuards.length })}</div>
                  {offlineGuards.length ? offlineGuards.map((guard) => (
                    <React.Fragment key={getGuardKey(guard, 'offline-card')}>
                      {renderGuardCard(guard)}
                    </React.Fragment>
                  )) : <p className="text-muted">{t('No offline guards')}</p>}
                </div>
              </>
            )}
          </div>
        </aside>
      </div>}
    </div>
  );
}
